import React from "react";
import { 
  Cloud,
  Database,
  Layers,
  Server
} from 'lucide-react';

const skillCategories = [
  {
    title: "Backend Development",
    skills: [
      { name: "Java 8/11/17/21" },
      { name: "Spring Boot" },
      { name: "Spring Cloud" },
      { name: "Microservices" },
      { name: "REST/GraphQL" },
      { name: "Hibernate/JPA" }
    ]
  },
  {
    title: "Frontend & Mobile",
    skills: [
      { name: "React" },
      { name: "TypeScript" },
      { name: "Next.js" },
      { name: "Tailwind CSS" },
      { name: "React Native" },
      { name: "Responsive Design" }
    ]
  },
  {
    title: "Cloud & DevOps",
    skills: [
      { name: "AWS" },
      { name: "Docker" },
      { name: "Kubernetes" },
      { name: "Terraform" },
      { name: "GitHub Actions" },
      { name: "CI/CD" }
    ]
  },
  {
    title: "Data & Messaging",
    skills: [
      { name: "PostgreSQL" },
      { name: "MongoDB" },
      { name: "Redis" },
      { name: "Kafka" },
      { name: "Elasticsearch" },
      { name: "Data Modeling" }
    ]
  }
];


const SkillsAndExpertise = () => {
  const categoryIcons = [
    <Server className="w-6 h-6" />,
    <Layers className="w-6 h-6" />,
    <Cloud className="w-6 h-6" />,
    <Database className="w-6 h-6" />
  ];

  const gradients = [
    'from-blue-500/10 to-blue-900/10',
    'from-emerald-500/10 to-emerald-900/10',
    'from-purple-500/10 to-purple-900/10',
    'from-amber-500/10 to-amber-900/10'
  ];

  return (
    <section id="skills" className="relative py-20 overflow-hidden" style={{ backgroundColor: 'rgb(2, 6, 23)' }}>
      <div className="absolute inset-0 opacity-5 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"></div>
      
      {/* Background pattern only */}
      
      <div className="relative container mx-auto px-4 md:px-6">
        <div className="text-center mb-16">
          <span className="inline-block mb-4 text-sm font-semibold text-blue-400 uppercase tracking-wider">My Abilities</span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Skills & <span className="text-transparent bg-clip-text bg-gradient-to-r from-[rgb(239,171,10)] to-yellow-400">Expertise</span>
          </h2>
          <div className="w-20 h-1 bg-gradient-to-r from-[rgb(239,171,10)] to-[rgb(255,200,50)] mx-auto"></div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 relative z-10 max-w-6xl mx-auto">
          {skillCategories.slice(0, 4).map((category, index) => (
            <div 
              key={index}
              className={`group relative bg-gradient-to-br from-slate-800/80 to-slate-900/90 rounded-2xl p-8 border border-white/5 shadow-xl hover:shadow-2xl hover:shadow-[rgb(239,171,10)]/10 transition-all duration-500 hover:-translate-y-1 overflow-hidden ${gradients[index]}`}
              style={{
                '--tw-ring-color': 'rgba(239, 171, 10, 0.1)',
                '--tw-ring-offset-shadow': '0 0 #0000',
                '--tw-ring-shadow': '0 0 0 1px rgba(239, 171, 10, 0.1)'
              } as React.CSSProperties}
            >
              {/* Removed gradient accent */}
              
              {/* Animated border effect */}
              <div className="absolute inset-0 border border-transparent group-hover:border-[rgb(239,171,10)]/30 rounded-2xl transition-all duration-500"></div>
              
              <div className="relative z-10 h-full flex flex-col">
                <div className="flex items-start justify-between mb-6">
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-br from-[rgb(239,171,10)] to-yellow-500 flex items-center justify-center text-white shadow-lg transform group-hover:rotate-6 transition-transform duration-500`}>
                    {categoryIcons[index]}
                  </div>
                  <div className="text-xs font-mono text-slate-400 bg-slate-800/50 px-3 py-1 rounded-full border border-white/5">
                    {index + 1}/0{skillCategories.length}
                  </div>
                </div>
                
                <h3 className="text-2xl font-bold text-white mb-5 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-[rgb(239,171,10)] group-hover:to-yellow-400 transition-all duration-500">
                  {category.title}
                </h3>
                
                <div className="grid grid-cols-2 gap-3 mt-2">
                  {category.skills.map((skill, skillIndex) => (
                    <div 
                      key={skillIndex} 
                      className="flex items-center px-4 py-2.5 bg-white/5 rounded-lg border border-white/5 hover:border-[rgb(239,171,10)]/30 transition-all duration-300 group-hover:bg-white/10"
                    >
                      <div className="w-2 h-2 rounded-full bg-[rgb(239,171,10)] mr-3 flex-shrink-0"></div>
                      <span className="text-slate-300 text-sm font-medium">{skill.name}</span>
                      <svg 
                        className="w-4 h-4 ml-auto text-[rgb(239,171,10)] opacity-0 group-hover:opacity-100 transition-opacity duration-300" 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24" 
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
                      </svg>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Hover overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl"></div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default SkillsAndExpertise;
